import Cookie from 'js-cookie';
import Router from 'next/router';
import { parseCookies, setCookie } from 'nookies';
import { put, call, takeLatest, select } from 'redux-saga/effects';
import { toast } from 'react-toastify';
import { axiosInstance } from '../../utils';

import {
  DEFINE_USER_COORDINATES_REQUEST,
  DEFINE_USER_COORDINATES_SUCCESS,
  DEFINE_USER_COORDINATES_FAILURE,
  LOGIN_REQUEST,
  LOGIN_SUCCESS,
  LOGIN_FAILURE,
  LOGOUT_REQUEST,
  LOGOUT_SUCCESS,
  LOGOUT_FAILURE,
  FETCH_USER_REQUEST,
  FETCH_USER_SUCCESS,
  FETCH_USER_FAILURE,
  RETRIEVE_PASSWORD_REQUEST,
  RETRIEVE_PASSWORD_SUCCESS,
  RETRIEVE_PASSWORD_FAILURE,
  SIGNUP_REQUEST,
  SIGNUP_SUCCESS,
  SIGNUP_FAILURE,
  SOCIAL_REQUEST,
  SOCIAL_SUCCESS,
  SOCIAL_FAILURE,
  UPLOAD_AVATAR_REQUEST,
  UPLOAD_AVATAR_SUCCESS,
  UPLOAD_AVATAR_FAILURE,
  REMOVE_AVATAR_REQUEST,
  REMOVE_AVATAR_SUCCESS,
  REMOVE_AVATAR_FAILURE,
  UPDATE_PROFILE_REQUEST,
  UPDATE_PROFILE_SUCCESS,
  UPDATE_PROFILE_FAILURE,
  UPDATE_PASSWORD_REQUEST,
  UPDATE_PASSWORD_SUCCESS,
  UPDATE_PASSWORD_FAILURE,
  RESET_PASSWORD_REQUEST,
  RESET_PASSWORD_SUCCESS,
  RESET_PASSWORD_FAILURE,
  CANCEL_SUBSCRIPTION_REQUEST,
  CANCEL_SUBSCRIPTION_SUCCESS,
  CANCEL_SUBSCRIPTION_FAILURE,
  MODAL_CLOSE,
  CHECKOUT_PASS_SUCCESS,
  CHECKOUT_PAYMENT_FREE,
  ADD_CREDIT_REQUEST,
  ADD_CREDIT_SUCCESS,
  ADD_CREDIT_FAILURE,
  FETCH_USER_CARDS_REQUEST,
  FETCH_USER_CARDS_SUCCESS,
  FETCH_USER_CARDS_FAILURE,
  SELECT_PAYMENT_METHOD,
  CANCEL_TICKET_SUCCESS,
  CHECKOUT_PAYMENT_USE_CREDITS,
  CHECK_PROMO_CODE_INITIAL,
  CHECK_PROMO_CODE_FAILURE
} from '../types';

function* resetPassword({ payload }) {
  const { resetToken, newPassword } = payload;

  try {
    yield call(axiosInstance.post, 'users/reset_password/new_password', { resetToken, newPassword });
    yield put({ type: RESET_PASSWORD_SUCCESS });

    setTimeout(() => {
      Cookie.set('withLogin', 'true', { path: '/' });
      Router.push('/');
    }, 5000);
  } catch (error) {
    yield put({ type: RESET_PASSWORD_FAILURE, payload: 'Look\'s like you already reset your password' });
  }
}

function* fetchCards(jwt) {
  try {
    const { data: fetchedCards } = yield call(axiosInstance.get, 'payment/stripe/cards', jwt && {
      headers: {
        'X-Access-Token': jwt,
      }
    });

    const transformedCards = fetchedCards.map(card => ({
      label: `Ending in •••• ${card.last4} (Expires ${card.expMonth}/${card.expYear})`,
      brand: card.brand,
      value: card.id,
    }));

    if (fetchedCards.length > 0) {
      yield put({ type: FETCH_USER_CARDS_SUCCESS, payload: {
        options: transformedCards,
      }});
      yield put({ type: SELECT_PAYMENT_METHOD, payload: transformedCards[0] });
    } else {
      yield put({ type: FETCH_USER_CARDS_FAILURE });
    }
  } catch (error) {
    yield put({ type: FETCH_USER_CARDS_FAILURE });
  }
}

function* cancelSubscription() {
  const subscriptionId = yield select(state => state.user.userData.subscription.stripeSubscription);

  try {
    const { data: { status, endDate } } = yield call(axiosInstance.post, 'subs/set/cancel', { subscriptionId });
    yield put({ type: CANCEL_SUBSCRIPTION_SUCCESS, payload: { status, endDate } });
    yield put({ type: MODAL_CLOSE });
  } catch (error) {
    yield put({ type: CANCEL_SUBSCRIPTION_FAILURE });
  }
}

function* updateUserProfile({ payload }) {
  const { firstName, lastName, phone, email, market: { value } } = payload;

  try {
    const { data } = yield call(axiosInstance.post, 'users/info', {
      firstName,
      lastName,
      phone,
      email,
      market: value,
    });

    yield put({ type: UPDATE_PROFILE_SUCCESS, payload: data.userInfo });
    toast.success('Successfully updated user profile');
  } catch (error) {
    yield put({ type: UPDATE_PROFILE_FAILURE, payload: error });
    toast.error('Something went wrong');
  }
}

function* updateUserPassword({ payload }) {
  const { oldPassword, newPassword } = payload;

  try {
    yield call(axiosInstance.post, 'users/update_password', {
      oldPassword,
      newPassword,
    });
    yield put({ type: UPDATE_PASSWORD_SUCCESS });
    Cookie.remove('new');
    toast.success('Successfully changed your password');
  } catch (error) {
    yield put({ type: UPDATE_PASSWORD_FAILURE, payload: error });
    toast.error('Something went wrong');
  }
}

function* updateUserAvatar({ payload }) {
  try {
    const { data } = yield call(axiosInstance.post, 'users/avatar', payload, {
      headers: {
        'Content-Type': 'multipart/form-data',
      }
    });
    yield put({ type: UPLOAD_AVATAR_SUCCESS, payload: data.avatar });
  } catch (error) {
    yield put({ type: UPLOAD_AVATAR_FAILURE, payload: error });
    toast.error('Something went wrong. Image must be less than 2MB');
  }
}

function* removeUserAvatar() {
  try {
    yield call(axiosInstance.delete, 'users/avatar');
    yield put({ type: REMOVE_AVATAR_SUCCESS });
  } catch (error) {
    yield put({ type: REMOVE_AVATAR_FAILURE, payload: error });
    toast.error('Something went wrong');
  }
}

function* socialAuthorization({ payload }) {
  const { code, type } = payload;

  try {
    const { data } = yield call(axiosInstance.get, `users/${type}/return?code=${code}`);
    const { token, refreshToken, userInfo } = data;

    Cookie.set('jwt', token, { path: '/' });
    Cookie.set('refreshToken', refreshToken, { path: '/' });
    Cookie.set('remember', true, { path: '/' });

    yield put({ type: FETCH_USER_CARDS_REQUEST });
    yield put({ type: SOCIAL_SUCCESS, payload: userInfo });
  } catch (error) {
    yield put({ type: SOCIAL_FAILURE });
  }
}

const getLocation = () => new Promise((res, rej) => {
  navigator.geolocation.getCurrentPosition(result => res(result),  error => rej(error));
});

function* defineUserCoordinates() {
  try {
   const { coords } =  yield call(getLocation);
   const { latitude, longitude } = coords;
   yield put({ type: DEFINE_USER_COORDINATES_SUCCESS, payload: { latitude, longitude } });
  } catch (error) {
    yield put({ type: DEFINE_USER_COORDINATES_FAILURE });
  }
}

function* userRegistration({ payload: { email } }) {
  const userLocation = yield select(state => state.user.location);
  try {
    const { data: { signup } } = yield call(axiosInstance.post, 'users/register', { email, latitude: userLocation.wasFetched && userLocation.latitude, longitude: userLocation.wasFetched && userLocation.longitude  });
    const { token, refreshToken, userInfo } = signup;

    Cookie.set('remember', true, { path: '/' });
    Cookie.set('jwt', token, { path: '/' });
    Cookie.set('refreshToken', refreshToken, { path: '/' });
    Cookie.set('new', 'true', { path: '/' });

    yield put({ type: SIGNUP_SUCCESS, payload: userInfo });

    yield put({ type: MODAL_CLOSE });
  } catch (error) {
    yield put({ type: SIGNUP_FAILURE, payload: 'User with this email already exists' });
  }
}

function* userLogin({ payload: { remember, formData } }) {
  try {
    const { data } = yield call(axiosInstance.post, 'users/login', formData);
    const { token, refreshToken, userInfo } = data;

    yield Cookie.set('jwt', token, { path: '/' });
    yield Cookie.set('refreshToken', refreshToken, { path: '/' });

    yield put({ type: FETCH_USER_CARDS_REQUEST });
    yield put({ type: LOGIN_SUCCESS, payload: userInfo });
    yield put({ type: MODAL_CLOSE, payload: 'login' });

    if (remember) {
      Cookie.set('remember', true);
    } else {
      Cookie.set('remember', false);
    }
  } catch (error) {
    yield put({ type: LOGIN_FAILURE, payload: 'Incorrect email or password' });
  }
}

function* updateUserSession({ payload }) {
  let cookies;

  if (typeof window === 'undefined') {
    cookies = parseCookies(payload);
  } else {
    cookies = {
      remember: Cookie.get('remember'),
      jwt: Cookie.get('jwt'),
      refreshToken: Cookie.get('refreshToken')
    }
  }

  const { remember, jwt, refreshToken } = cookies;

  if (remember === 'true') {
    try {
      const { data } = yield call(axiosInstance.get, 'users/info', {
        headers: { 'X-Access-Token': jwt },
      });
      yield call(fetchCards, jwt);
      yield put({ type: FETCH_USER_SUCCESS, payload: data });

      if (typeof window !== 'undefined') {
        yield put({ type: MODAL_CLOSE });
      }
    } catch (error) {
      try {
        const { data } = yield call(axiosInstance.post, 'users/refresh', { refreshToken });
        const { token: newJWTToken, refreshToken: newRefreshToken, userInfo } = data;

        yield call(fetchCards, newJWTToken);
        yield put({ type: FETCH_USER_SUCCESS, payload: userInfo });

        if (typeof window === 'undefined') {
          yield setCookie(payload, 'jwt', newJWTToken, { path: '/' });
          yield setCookie(payload, 'refreshToken', newRefreshToken, { path: '/' });
        } else {
          yield Cookie.set('jwt', newJWTToken, { path: '/' });
          yield Cookie.set('refreshToken', newRefreshToken, { path: '/' });
          yield put({ type: MODAL_CLOSE });
        }
      } catch (err) {
        yield put({ type: FETCH_USER_FAILURE });
      }
    }
  } else {
    yield put({ type: FETCH_USER_FAILURE, error: 'Something went wrong' });
  }
}

function* userForgotPassword({ payload: { email }}) {
  try {
   yield call(axiosInstance.post, 'users/reset_password', { email });
   yield put({ type: RETRIEVE_PASSWORD_SUCCESS });
  } catch (error) {
   yield put({ type: RETRIEVE_PASSWORD_FAILURE, payload: 'Please provide valid email' });
  }
}

function* userLogout() {
  try {
    yield call(axiosInstance.post, 'users/logout');

    Cookie.remove('remember', false);
    Cookie.remove('jwt', false);
    Cookie.remove('refreshToken', false);

    yield put({ type: LOGOUT_SUCCESS });
    Cookie.remove('new');
  } catch (error) {
    yield put({ type: LOGOUT_FAILURE });
  }
}

function* addCredits({ payload: { promoCode, source } }) {
  try {
    const { data } = yield call(axiosInstance.post, 'credits/deposit', { promoCode });
    yield put({ type: CHECKOUT_PAYMENT_USE_CREDITS });
    yield put({ type: CHECK_PROMO_CODE_INITIAL });
    yield put({ type: ADD_CREDIT_SUCCESS, payload: data });
  } catch (error) {
    if (source === 'checkout') {
      yield put({ type: CHECK_PROMO_CODE_FAILURE, payload: error.message });
    } else {
      yield put({ type: ADD_CREDIT_FAILURE, payload: 'Code not found' });
    }
  }
}

export default function* saga() {
  yield takeLatest(SOCIAL_REQUEST, socialAuthorization);
  yield takeLatest(DEFINE_USER_COORDINATES_REQUEST, defineUserCoordinates);
  yield takeLatest(SIGNUP_REQUEST, userRegistration);
  yield takeLatest(LOGIN_REQUEST, userLogin);
  yield takeLatest(RETRIEVE_PASSWORD_REQUEST, userForgotPassword);
  yield takeLatest([FETCH_USER_REQUEST, CHECKOUT_PASS_SUCCESS, ADD_CREDIT_SUCCESS, CHECKOUT_PAYMENT_FREE, CANCEL_TICKET_SUCCESS], updateUserSession);
  yield takeLatest(LOGOUT_REQUEST, userLogout);
  yield takeLatest(UPDATE_PROFILE_REQUEST, updateUserProfile);
  yield takeLatest(UPDATE_PASSWORD_REQUEST, updateUserPassword);
  yield takeLatest(UPLOAD_AVATAR_REQUEST, updateUserAvatar);
  yield takeLatest(REMOVE_AVATAR_REQUEST, removeUserAvatar);
  yield takeLatest(RESET_PASSWORD_REQUEST, resetPassword);
  yield takeLatest(CANCEL_SUBSCRIPTION_REQUEST, cancelSubscription);
  yield takeLatest(ADD_CREDIT_REQUEST, addCredits);
  yield takeLatest(FETCH_USER_CARDS_REQUEST, fetchCards);
}

